import Breadcrumbs from "@/components/Breadcrumbs";
import { CommentsService, ElementDatabaseService, PageService } from "@/services/admin-services";
import { DatabaseElement, ElementComment, Page } from "@/services/admin-services/models";
import { LocaleService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import { PageType } from "@/services/shared-models";
import { Dictionary } from "@/utils/Dictionary";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    FormGroup,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    alpha,
    useTheme,
} from "@mui/material";
import { groupBy } from "lodash";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const breadcrumbs = [
    {
        to: "..",
        title: "Administration",
    },
    {
        title: "Element Comments",
        to: "",
    },
];

export interface ElementInfo {
    slug: string;
    name: string;
}

const Overview = () => {
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [comments, setComments] = useState<ElementComment[]>([]);
    const [languages, setLanguages] = useState<Array<Language>>([]);
    const [filterRequireApproval, setFilterRequireApproval] = useState<boolean>(true);

    const navigate = useNavigate();
    const theme = useTheme();

    const commentsService = useMemo(() => new CommentsService("int"), []);
    const pagesService = useMemo(() => new PageService("int"), []);
    const elementService = useMemo(() => new ElementDatabaseService("int", "int-en"), []);

    const elementPageSlugs = useRef<Dictionary<string>>({});
    const elementSlugs = useRef<Dictionary<ElementInfo>>({});

    useEffect(() => {
        Promise.all([
            commentsService.getElementComments().then((comments) => {
                setComments(comments);
            }),
            LocaleService.get().then(parseLanguages),
            pagesService.getPages().then(handlePages),
            elementService.get().then(handleElements),
        ]).then(() => setIsLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const grouped = useMemo(
        () =>
            isLoading
                ? {}
                : groupBy(
                      (filterRequireApproval ? comments.filter((x) => x.unreadComments > 0) : comments).sort((a, b) => {
                          const left = elementSlugs.current[a.elementId]?.name || "";
                          const right = elementSlugs.current[b.elementId]?.name || "";

                          return left.localeCompare(right);
                      }),
                      (x) => x.elementId
                  ),
        [comments, filterRequireApproval, isLoading]
    );

    const parseLanguages = (regions: Array<Locale>) => {
        const languages: Array<Language> = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({
                    ...language,
                    slug: `int-${split[1]}`,
                    regionLabel: "International",
                });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const handlePages = (pages: Array<Page>) => {
        const elementPages = pages.filter((x) => x.pageType === PageType.Elements);
        elementPages.forEach((page) => {
            elementPageSlugs.current[page.language] = page.slug;
        });
    };

    const handleElements = (elements: Array<DatabaseElement>) => {
        elements.forEach((element) => {
            if (element.name && element.slug)
                elementSlugs.current[element.id] = { slug: element.slug, name: element.name };
        });
    };

    const renderLanguageDetails = (item: ElementComment) => {
        return (
            <>
                <TableCell component="td" scope="row">
                    <Typography color={"text.secondary"} variant={"subtitle2"}>
                        {languages.find((x) => x.slug === item.languageCode)?.label || "-"}
                    </Typography>
                </TableCell>
                <TableCell component="td" scope="row">
                    <Typography
                        color={"text.secondary"}
                        variant={"subtitle2"}
                        sx={{ display: "flex", alignItems: "center" }}
                    >
                        {item.totalComments}{" "}
                        {item.unreadComments > 0 && (
                            <Typography
                                variant={"caption"}
                                fontWeight={700}
                                sx={{
                                    bgcolor: alpha(theme.palette.warning.light, 0.1),
                                    color: theme.palette.warning.dark,
                                    paddingX: 1.5,
                                    paddingY: 0.5,
                                    borderRadius: 4,
                                    display: "inline",
                                    ml: 3,
                                }}
                            >
                                {`${item.unreadComments} unread`}
                            </Typography>
                        )}
                    </Typography>
                </TableCell>
                <TableCell scope="row" sx={{ p: 0 }}>
                    <Box sx={{ display: "flex" }}>
                        <Button
                            color={"primary"}
                            sx={{ ml: "auto", mr: 1 }}
                            onClick={() => navigate(`/admin/element-comments/${item.elementId}/${item.languageCode}`)}
                        >
                            Manage comments
                        </Button>
                        <Button
                            component={"a"}
                            color={"secondary"}
                            sx={{ ml: 1, mr: 1 }}
                            target="_blank"
                            href={`/${item.languageCode.replace("int", "be")}/${
                                elementPageSlugs.current[item.languageCode]
                            }/${elementSlugs.current[item.elementId]?.slug}`}
                        >
                            View on page
                        </Button>
                    </Box>
                </TableCell>
            </>
        );
    };

    return (
        <Box>
            <Breadcrumbs trail={breadcrumbs} />

            <Divider />

            <Grid container spacing={4} p={4}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <FormGroup sx={{ alignContent: "flex-end" }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={filterRequireApproval}
                                    onChange={(e) => setFilterRequireApproval(e.target.checked)}
                                />
                            }
                            label={<Typography variant="body2">Only show unread comments</Typography>}
                            sx={{ mr: 0 }}
                        />
                    </FormGroup>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 750 }} aria-label="simple table">
                            <TableHead sx={{ bgcolor: "alternate.main" }}>
                                <TableRow>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Element Name
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Language
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            color={"text.secondary"}
                                            variant={"caption"}
                                            fontWeight={700}
                                            sx={{ textTransform: "uppercase" }}
                                        >
                                            Comments
                                        </Typography>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={9}
                                            sx={{
                                                height: 400,
                                                alignItems: "center",
                                                flexDirection: "column",
                                            }}
                                        >
                                            <Box sx={{ display: "flex", placeContent: "center" }}>
                                                <CircularProgress />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    Object.keys(grouped).map((key, i) => {
                                        const rows = grouped[key];

                                        return (
                                            <Fragment key={i}>
                                                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                                    <TableCell
                                                        component="th"
                                                        scope="row"
                                                        rowSpan={rows.length}
                                                        sx={{ verticalAlign: "top" }}
                                                    >
                                                        <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                            {elementSlugs.current[rows[0].elementId]?.name}
                                                        </Typography>
                                                    </TableCell>
                                                    {renderLanguageDetails(rows[0])}
                                                </TableRow>
                                                {rows.slice(1).map((row, i) => (
                                                    <TableRow key={i}>{renderLanguageDetails(row)}</TableRow>
                                                ))}
                                            </Fragment>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Overview;
